import CryptoJS from "crypto-js";
import {
	collection,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	updateDoc,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { db } from "../firebaseConfig";
import Tooltip from "./Tooltip";

const TherapyHome = () => {
	const navigate = useNavigate();
	const { user, encryptionKey } = useUser();
	const [sessionSummaries, setSessionSummaries] = useState([]);
	const [runTour, setRunTour] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const TherapySteps = [
		{
			target: ".therapy-home-section",
			content:
				"Welcome to your therapy space! Here you can see summaries of all your past therapy sessions.",
			placement: "center",
		},
		{
			target: ".add-session-button",
			content: "Start a new therapy session here!",
			placement: "top",
		},
	];

	useEffect(() => {
		const setDemoState = async () => {
			const userRef = doc(db, "Users", user.uid);
			const userDoc = await getDoc(userRef);
			if (userDoc.exists()) {
				const userData = userDoc.data();
				if (!userData.therapyHomeDemo) {
					setRunTour(true);
					await updateDoc(userRef, { therapyHomeDemo: true });
				}
			}
		};

		const fetchSessionSummaries = async () => {
			try {
				if (!user) return;
				setIsLoading(true);
				const sessionsRef = collection(
					db,
					"Users",
					user.uid,
					"sessions"
				);
				const q = query(
					sessionsRef,
					orderBy("createdAt", "desc"),
					limit(15)
				);
				const querySnapshot = await getDocs(q);

				const summaries = [];
				querySnapshot.forEach((doc) => {
					const data = doc.data();
					if (data.summary) {
						const decryptedSummary = CryptoJS.AES.decrypt(
							data.summary,
							encryptionKey
						).toString(CryptoJS.enc.Utf8);

						summaries.push({
							id: doc.id,
							summary: decryptedSummary,
							createdAt: data.createdAt,
						});
					}
				});
				setSessionSummaries(summaries);
			} catch (error) {
				console.error("Error fetching session summaries:", error);
			} finally {
				setIsLoading(false);
			}
		};

		setDemoState();
		fetchSessionSummaries();
	}, [user, encryptionKey]);

	const startNewSession = () => {
		navigate("/therapy");
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 therapy-home-section">
			<Joyride
				steps={TherapySteps}
				run={runTour}
				continuous={true}
				showSkipButton={true}
				showProgress={false}
				styles={{
					options: {
						primaryColor: "#60a5fa",
						textColor: "#374151",
					},
					buttonClose: {
						display: "none",
					},
				}}
				locale={{
					last: "Start my therapy journey!",
				}}
			/>
			<div className="w-full max-w-6xl h-[calc(var(--vh,1vh)*85)] backdrop-blur-lg rounded-3xl shadow-xl overflow-auto flex bg-white bg-opacity-90">
				<div className="flex-1 p-6">
					<div className="flex justify-center items-center mb-4 py-4">
						<h1 className="text-3xl font-light text-gray-800">
							Therapy
						</h1>
						<Tooltip text="Start new session">
							<button
								onClick={startNewSession}
								className="ml-5 bg-blue-600 text-xl text-white px-2 rounded-full hover:bg-blue-900 transition-all duration-300 add-session-button">
								+
							</button>
						</Tooltip>
					</div>
					<div className="max-w-5xl mx-auto">
						{isLoading ? (
							<div className="flex items-center justify-center min-h-[200px]">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
							</div>
						) : (
							<>
								{sessionSummaries.map((session) => (
									<div
										key={session.id}
										className="mb-6 relative pl-4 before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-blue-200">
										<div className="bg-white rounded-xl  p-5 hover:shadow-md transition-all duration-300">
											<div className="flex justify-between items-center mb-3">
												<span className="text-sm font-light text-blue-600">
													{session.createdAt
														?.toDate instanceof
													Function
														? session.createdAt
																.toDate()
																.toLocaleDateString(
																	"en-US",
																	{
																		weekday:
																			"long",
																		year: "numeric",
																		month: "long",
																		day: "numeric",
																	}
																)
														: "No date available"}
												</span>
											</div>
											<p className="text-gray-700 whitespace-pre-wrap">
												{session.summary}
											</p>
										</div>
									</div>
								))}
								<div className="mt-2 text-white text-center">
									-----
								</div>

								{sessionSummaries.length === 0 && (
									<div className="text-center text-gray-500 mt-2">
										No therapy sessions yet. Start your
										first session to begin your journey!
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TherapyHome;
